import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import { clients, type ClientInput } from '@/api/endpoints';
import type { Client } from '@/types';
import { Icon } from './Icon';
import { SearchableSelect } from './SearchableSelect';
import { CoordsInput } from './CoordsInput';
import { VOIVODESHIPS, COUNTIES } from '@/data/poland-geo';
import { gminyFor } from '@/data/poland-gmina';

interface Props {
  open: boolean;
  initial?: Client | null;
  onClose: () => void;
  onSaved: (client: Client) => void;
}

const EMPTY: ClientInput = {
  name: '', nip: '', phone: '', email: '',
  addressLine1: '', city: '', postalCode: '',
  voivodeship: '', county: '', gmina: '',
  latitude: null, longitude: null, locationNote: '',
  remarks: '',
};

export function ClientFormModal({ open, initial, onClose, onSaved }: Props) {
  const [data, setData] = useState<ClientInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Klient, który ma już ten NIP. Backend i tak zablokuje zapis — chodzi o to, żeby użytkownik
  // dowiedział się o tym przy polu NIP, zanim wypełni resztę formularza.
  const [nipTaken, setNipTaken] = useState<Client | null>(null);

  const nipDigits = (data.nip ?? '').replace(/\D/g, '');
  useEffect(() => {
    if (!open || nipDigits.length !== 10) { setNipTaken(null); return; }
    let porzucone = false;
    const h = setTimeout(() => {
      clients.list(nipDigits)
        .then(found => {
          if (porzucone) return;
          const kolizja = found.find(c =>
            (c.nip ?? '').replace(/\D/g, '') === nipDigits && c.id !== initial?.id);
          setNipTaken(kolizja ?? null);
        })
        .catch(() => { if (!porzucone) setNipTaken(null); });
    }, 400);
    return () => { porzucone = true; clearTimeout(h); };
  }, [open, nipDigits, initial?.id]);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null);
    setBusy(false);
    if (initial) {
      setData({
        name: initial.name ?? '',
        nip: initial.nip ?? '',
        phone: initial.phone ?? '',
        email: initial.email ?? '',
        addressLine1: initial.addressLine1 ?? '',
        city: initial.city ?? '',
        postalCode: initial.postalCode ?? '',
        voivodeship: initial.voivodeship ?? '',
        county: initial.county ?? '',
        gmina: initial.gmina ?? '',
        latitude: initial.latitude ?? null,
        longitude: initial.longitude ?? null,
        locationNote: initial.locationNote ?? '',
        remarks: initial.remarks ?? '',
      });
    } else {
      setData(EMPTY);
    }
  }, [open, initial]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: data, onClose, open, busy,
  });

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.name.trim()) { setErr('Nazwa klienta jest wymagana'); return; }
    // Backend odrzuci pół pary, ale komunikat po stronie formularza jest szybszy i konkretniejszy.
    if ((data.latitude == null) !== (data.longitude == null)) {
      setErr('Podaj obie współrzędne albo żadnej');
      return;
    }
    setBusy(true);
    setErr(null);
    const payload: ClientInput = {
      name: data.name.trim(),
      nip: data.nip?.trim() || undefined,
      phone: data.phone?.trim() || undefined,
      email: data.email?.trim() || undefined,
      addressLine1: data.addressLine1?.trim() || undefined,
      city: data.city?.trim() || undefined,
      postalCode: data.postalCode?.trim() || undefined,
      voivodeship: data.voivodeship?.trim() || undefined,
      county: data.county?.trim() || undefined,
      gmina: data.gmina?.trim() || undefined,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      locationNote: data.locationNote?.trim() || null,
      remarks: data.remarks?.trim() || undefined,
    };
    const op = initial ? clients.update(initial.id, payload) : clients.create(payload);
    op.then(c => { onSaved(c); onClose(); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  const set = (k: keyof ClientInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setData({ ...data, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {initial ? 'Edytuj klienta' : 'Nowy klient'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="field col-span-2">
            <span className="field-label">Nazwa *</span>
            <input value={data.name} onChange={set('name')} className="input" placeholder="np. Gmina Wiśniówka" autoFocus />
          </label>
          <label className="field">
            <span className="field-label">NIP</span>
            <input value={data.nip ?? ''} onChange={set('nip')}
                   className={'input font-mono' + (nipTaken ? ' !border-red-400 bg-red-50' : '')}
                   placeholder="1234567890" maxLength={13} />
            {nipTaken && (
              <span className="mt-1 text-[11px] text-red-600">
                Ten NIP ma już klient „{nipTaken.name}".
              </span>
            )}
          </label>
          <label className="field">
            <span className="field-label">Telefon</span>
            <input value={data.phone ?? ''} onChange={set('phone')} className="input" placeholder="+48 …" />
          </label>
          <label className="field col-span-2">
            <span className="field-label">E-mail</span>
            <input type="email" value={data.email ?? ''} onChange={set('email')} className="input" placeholder="biuro@…" />
          </label>
          <label className="field col-span-2">
            <span className="field-label">Ulica i nr</span>
            <input value={data.addressLine1 ?? ''} onChange={set('addressLine1')} className="input" placeholder="ul. Wodna 12" />
          </label>
          <label className="field">
            <span className="field-label">Kod pocztowy</span>
            <input value={data.postalCode ?? ''} onChange={set('postalCode')} className="input font-mono" placeholder="00-000" />
          </label>
          <label className="field">
            <span className="field-label">Miejscowość</span>
            <input value={data.city ?? ''} onChange={set('city')} className="input" placeholder="Kielce" />
          </label>
          <label className="field">
            <span className="field-label">Województwo</span>
            <SearchableSelect value={data.voivodeship ?? ''}
              onChange={v => setData(d => ({ ...d, voivodeship: v,
                county: d.county && !(COUNTIES[v]?.includes(d.county)) ? '' : d.county,
                gmina: d.county && !(COUNTIES[v]?.includes(d.county)) ? '' : d.gmina }))}>
              <option value="">— wybierz —</option>
              {VOIVODESHIPS.map(v => <option key={v} value={v}>{v}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Powiat</span>
            <SearchableSelect value={data.county ?? ''} disabled={!data.voivodeship}
              onChange={v => setData(d => ({ ...d, county: v, gmina: '' }))}>
              <option value="">{data.voivodeship ? '— wybierz —' : '— najpierw województwo —'}</option>
              {(COUNTIES[data.voivodeship ?? ''] ?? []).map(c => <option key={c} value={c}>{c}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Gmina</span>
            <SearchableSelect value={data.gmina ?? ''} disabled={!data.county} clearable allowFreeValue
              onChange={v => setData(d => ({ ...d, gmina: v }))}
              placeholder={data.county ? '— wybierz —' : '— najpierw powiat —'}>
              {gminyFor(data.voivodeship ?? '', data.county ?? '').map(g => <option key={g} value={g}>{g}</option>)}
            </SearchableSelect>
          </label>
          {/* Współrzędne są ODDZIELNE od adresu: siedziba z KRS bywa biurem gdzie indziej niż
              zakład, do którego naprawdę jeździ brygada. Pinezka na mapie prac bierze się stąd. */}
          <div className="col-span-2 border-t border-ink-100 pt-3 mt-1">
            <div className="text-[12px] text-ink-500 mb-1">
              Lokalizacja na mapie — punkt, do którego realnie jeździmy. Nie musi pokrywać się z adresem
              siedziby; zostaw puste, jeśli nie wiesz.
            </div>
          </div>
          <CoordsInput
            latitude={data.latitude}
            longitude={data.longitude}
            onChange={(lat, lng) => setData(d => ({ ...d, latitude: lat, longitude: lng }))}
            layout="stacked"
            label="Współrzędne klienta"
          />
          <label className="field col-span-2">
            <span className="field-label">Opis punktu</span>
            <input value={data.locationNote ?? ''} onChange={set('locationNote')} className="input"
                   placeholder="np. baza techniczna, SUW Kępy, biuro" />
          </label>

          <label className="field col-span-2">
            <span className="field-label">Uwagi</span>
            <textarea value={data.remarks ?? ''} onChange={set('remarks')} className="textarea" rows={3} />
          </label>
          {err && <div className="col-span-2 text-red-500 text-[12px]">Błąd: {err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : (initial ? 'Zapisz zmiany' : 'Dodaj klienta')}
          </button>
        </div>
      </form>
    </div>
  );
}
